import { createElement, useEffect, useRef, useState } from "react";
import { CircleMarker, Marker, Tooltip, useMap } from "react-leaflet";
import useMapElementStyles from "../useMapElementStyles";
import { updateMapElementById } from "../helperFunctions/updateMapElementById";
import { Icon, svg } from "leaflet";
import { Box, Typography } from "@mui/material";
import { numberToString } from "../../numberConverter";
import { useTranslation } from "react-i18next";
import { elevationColor } from "../helperFunctions/elevationColor";

// Result-view nodes are small and often sit under a (thick / animated) edge,
// which makes them hard to click. We give each result node an invisible, larger
// hit target on a dedicated pane ABOVE the edges so node clicks win, without
// enlarging the visible node. The hit target uses an SVG renderer on purpose:
// Leaflet sets pointer-events:none on the SVG container, so clicks on empty
// space still fall through to the edges below (a canvas renderer would swallow
// them). One shared renderer/pane per map, created lazily.
const RESULT_NODE_HIT_RADIUS = 11;
const hitRenderers = new WeakMap();
function getResultNodeHitRenderer(map) {
  let renderer = hitRenderers.get(map);
  if (!renderer) {
    if (!map.getPane("resultNodeHit")) {
      const pane = map.createPane("resultNodeHit");
      // above the overlay pane (400, edges + nodes) so node clicks win over
      // overlapping edges; below the marker pane (600) so outlet/pump icons stay
      // on top.
      pane.style.zIndex = 450;
    }
    renderer = svg({ pane: "resultNodeHit" });
    hitRenderers.set(map, renderer);
  }
  return renderer;
}

const Node = ({
  element,
  currentMode,
  onSelect,
  selectedElement,
  mapElements,
  setMapElements,
  onProjectAreaCheck,
  isInSelection,
  isDisconnected,
  isOverDepth,
  showElevation,
  elevationRange,
  elevationMetric = "ground",
  profileActive,
}) => {
  useEffect(() => {
    calcColor();
  }, [isInSelection, isDisconnected, isOverDepth, selectedElement, element]);
  const { t } = useTranslation();
  const mapStyles = useMapElementStyles();

  const [position, setPosition] = useState(element.position);
  const nodeRef = useRef(null);
  const map = useMap(); // Access the Leaflet map instance
  const isDraggingRef = useRef(false); // a drag is currently in progress
  const hasMovedRef = useRef(false); // the pointer actually moved during the drag
  const dragStartPosRef = useRef(null); // original position to restore on cancel
  const escHandlerRef = useRef(null); // keydown listener active during a drag
  const [draggable, setDraggable] = useState(false);
  const [color, setColor] = useState(mapStyles.node.colors.default);

  const radius = draggable
    ? mapStyles.node.size.draggable
    : mapStyles.node.size[currentMode]
      ? mapStyles.node.size[currentMode]
      : mapStyles.node.size.default;

  const isSelected = selectedElement.id === element.id;

  function calcColor() {
    var newColor =
      mapStyles.node.colors[currentMode] || mapStyles.node.colors.default;

    if (isOutlet) newColor = mapStyles.node.colors.isOutlet;
    // not saved
    if (!element.isSaved) newColor = mapStyles.node.colors.notSaved;
    // not connected to the main network (integrity check)
    if (isDisconnected) newColor = mapStyles.node.colors.disconnected;
    // buried depth exceeds the configured maximum (post-optimization check)
    if (isOverDepth) newColor = mapStyles.node.colors.overDepth;
    // "Select Nodes" mode: keep every pickable node highlighted so it stands
    // out and is easy to target (already-picked nodes handled below).
    if (currentMode === "selection")
      newColor = mapStyles.node.colors.selection;
    // is selected
    if (isSelected) newColor = mapStyles.node.colors.selected;
    // is in selection
    if (isInSelection) newColor = mapStyles.node.colors.inSelection;

    setColor(newColor);
  }

  const pumpProp =
    (element.props &&
      element.props.find((prop) => prop.key === "lift_station")) ||
    null;
  const isPump = pumpProp ? pumpProp.value : false;
  // is outlet Candidate
  const outletProp =
    (element.props &&
      element.props.find((prop) => prop.key === "outlet_candidate")) ||
    null;
  const isOutlet = outletProp ? outletProp.value : false;

  const fillOpacity = draggable
    ? mapStyles.node.fillOpacity.draggable
    : mapStyles.node.fillOpacity[currentMode]
      ? mapStyles.node.fillOpacity[currentMode]
      : mapStyles.node.fillOpacity.default;

  useEffect(() => {
    // while picking nodes for the elevation profile a click must only select,
    // never start a drag (the profile dialog opens on mousedown and swallows
    // the mouseup, which would leave the node stuck to the cursor)
    setDraggable(currentMode === "edit" && !profileActive);
  }, [currentMode, element, profileActive]);

  useEffect(() => {
    setPosition(element.position);
  }, [element]);

  // remove a dangling ESC listener if the node unmounts mid-drag
  useEffect(() => {
    return () => detachEscHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateNodes() {
    if (!nodeRef.current) return;
    const position = nodeRef.current.getLatLng();
    const newElement = {
      ...element,
      isSaved: false,
      position: [position.lat, position.lng],
    };
    setMapElements(updateMapElementById(mapElements, newElement));
  }

  function restoreUserSelect() {
    const mapContainer = map.getContainer();
    mapContainer.style.userSelect = "";
    mapContainer.style.webkitUserSelect = "";
    mapContainer.style.MozUserSelect = "";
    mapContainer.style.msUserSelect = "";
  }

  function detachEscHandler() {
    if (escHandlerRef.current) {
      document.removeEventListener("keydown", escHandlerRef.current);
      escHandlerRef.current = null;
    }
  }

  // abort an in-progress drag (ESC): restore the original position and do not
  // commit the move
  function cancelDrag() {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    hasMovedRef.current = false;
    map.removeEventListener("mousemove");
    map.dragging.enable();
    setPosition(dragStartPosRef.current || element.position);
    restoreUserSelect();
    detachEscHandler();
  }

  function handleMouseDown(e) {
    // Disable text selection on the map container or any specific element
    const mapContainer = map.getContainer();
    mapContainer.style.userSelect = "none";
    mapContainer.style.webkitUserSelect = "none"; // For Chrome, Safari, and Opera
    mapContainer.style.MozUserSelect = "none"; // For Firefox
    mapContainer.style.msUserSelect = "none";
    if (!draggable) return;
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    dragStartPosRef.current = element.position;
    // listen for ESC to cancel the drag
    const onKeyDown = (ev) => {
      if (ev.key === "Escape") cancelDrag();
    };
    escHandlerRef.current = onKeyDown;
    document.addEventListener("keydown", onKeyDown);
    map.dragging.disable();
    map.on("mousemove", function (e) {
      const { lat, lng } = e.latlng;
      const newPosition = [lat, lng];
      if (onProjectAreaCheck(newPosition)) {
        hasMovedRef.current = true;
        setPosition(newPosition);
        // live update of Edges on drag -> LAGGING
        //updateNodes();
      }
    });
  }

  function handleMouseUp() {
    // Enable text selection on the map container or the specific element after mouse up
    restoreUserSelect();
    detachEscHandler();
    if (!draggable) return;
    // drag was already aborted via ESC -> don't commit
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    map.removeEventListener("mousemove");
    map.dragging.enable();
    // only commit (and mark the node as modified) if it was actually dragged;
    // a plain click must not flip isSaved -> false and turn the node grey
    if (hasMovedRef.current) updateNodes();
    hasMovedRef.current = false;
  }

  const handleMouseOver = () => {
    if (nodeRef.current && !isOutlet && !isPump) {
      nodeRef.current.setStyle({
        color: mapStyles.node.colors.hover || mapStyles.node.colors.default,
      }); // Change color on hover
    }
  };

  const handleMouseOut = () => {
    if (nodeRef.current && !isOutlet && !isPump) {
      // reset to the actually displayed color, which in the elevation view is
      // the elevation-based color (not the plain default node color)
      nodeRef.current.setStyle({ color: displayColor }); // Reset color on mouseout
    }
  };

  const eventHandlers = {
    mousedown: (e) => {
      handleMouseDown(e);
      onSelect(element);
    },
    mouseover: handleMouseOver,
    mouseout: handleMouseOut,
    mouseup: () => handleMouseUp(draggable),
  };

  // custom Icons
  const iconRadius = radius + 7;
  const outfallCandidate = new Icon({
    iconUrl: isSelected
      ? "/assets/svg/map/outfall-selected.svg"
      : "/assets/svg/map/outfall.svg",
    iconSize: [iconRadius * 2, iconRadius * 2], // size of the icon
    iconAnchor: [iconRadius, iconRadius], // point of the icon which will correspond to marker's location
    popupAnchor: [-3, -76], // point from which the popup should open relative to the iconAnchor
  });

  const pump = new Icon({
    // a lift station placed on an over-depth node is flagged red (selection
    // still wins so click feedback stays visible), mirroring the node coloring
    iconUrl: isSelected
      ? "/assets/svg/map/pump-selected.svg"
      : isOverDepth
        ? "/assets/svg/map/pump-overdepth.svg"
        : "/assets/svg/map/pump.svg",
    iconSize: [iconRadius * 2, iconRadius * 2], // size of the icon
    iconAnchor: [iconRadius, iconRadius], // point of the icon which will correspond to marker's location
    popupAnchor: [-3, -76], // point from which the popup should open relative to the iconAnchor
  });

  // elevation source: ground (terrain, props) or — in the result view —
  // the calculated invert elevation (calc)
  const elevation =
    elevationMetric === "invert"
      ? element.calc?.find((obj) => obj.key === "invert_elevation")
      : element.props?.find((obj) => obj.key === "elevation");

  // "Visualize Elevation": color nodes by their elevation (blue = low → red =
  // high). Falls back to the normal color when the view is off or no elevation.
  const elevationValue =
    elevation != null && elevation.value !== "" && elevation.value != null
      ? parseFloat(elevation.value)
      : null;
  const displayColor =
    showElevation && elevationRange && elevationValue != null
      ? elevationColor(elevationValue, elevationRange.min, elevationRange.max)
      : color;

  return (
    <>
      {createElement(isOutlet || isPump ? Marker : CircleMarker, {
        // color/fillOpacity go through pathOptions so react-leaflet restyles the
        // layer in place (setStyle) instead of us remounting it on every
        // selection or color change — the main perf win with many nodes. (For
        // the Marker branch these are ignored; icon/position update reactively.)
        pathOptions: { color: displayColor, fillOpacity },
        ref: nodeRef,
        center: position,
        eventHandlers: eventHandlers,
        radius: radius,
        position: position,
        icon: isOutlet ? outfallCandidate : pump,
        children: element.name && (
          <Tooltip
            direction="bottom"
            offset={[0, iconRadius]}
            opacity={1}
            permanent={false}
          >
            <Typography> {`${t("Name")}: ${element.name}`}</Typography>
            {elevation && elevation.value != null && (
              <Typography>{`${t(
                elevationMetric === "invert"
                  ? "Invert Elevation"
                  : "Elevation"
              )}: ${numberToString(elevation.value)} ${elevation.unit}`}</Typography>
            )}
            {isOverDepth && (
              <Typography color="error">
                {t("Max. burial depth exceeded")}
              </Typography>
            )}
          </Tooltip>
        ),
      })}
      {currentMode === "view" && (
        <CircleMarker
          center={position}
          radius={RESULT_NODE_HIT_RADIUS}
          // invisible but hit-testable (SVG visiblePainted): no stroke, fully
          // transparent fill
          pathOptions={{ stroke: false, fill: true, fillOpacity: 0 }}
          renderer={getResultNodeHitRenderer(map)}
          bubblingMouseEvents={false}
          eventHandlers={{
            click: () => onSelect(element),
            // the hit target sits on top of the visible node, so forward hover
            // (color + tooltip) to it
            mouseover: () => {
              handleMouseOver();
              if (nodeRef.current && nodeRef.current.openTooltip)
                nodeRef.current.openTooltip();
            },
            mouseout: () => {
              handleMouseOut();
              if (nodeRef.current && nodeRef.current.closeTooltip)
                nodeRef.current.closeTooltip();
            },
          }}
        />
      )}
    </>
  );
};

export default Node;
